import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import useCartContext from "../hooks/useCartContext";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { COURSE_BASE_URL } from "../services/courseService";

// Load Stripe with your publishable key
const stripePromise = loadStripe(
  "pk_test_51ORppnHrF7WblQaxHls1l2XtTjVXUibByhp55ZZx7tImDJtZJnk0xzMTpiEM83vTJNl17zAbzddxBoQIj0aBt2Ab004E13nY9g"
);

// Checkout Form Component
const CheckoutForm = ({ totalAmount, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Create payment intent on your server

      //  "http://localhost:5000/api/create-payment-intent",
      const response = await fetch(
        `${COURSE_BASE_URL}/api/courses/create-payment-intent`,

        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: totalAmount,
            currency: "usd",
            metadata: {
              // Add any additional metadata you need
              //customer_id: "user_123", // Replace with actual user ID
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const { clientSecret } = await response.json();

      // Confirm the payment
      const { error: stripeError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: "Customer Name", // Replace with actual customer name
            },
          },
        });

      if (stripeError) {
        setError(stripeError.message);
        setIsProcessing(false);
        return;
      }

      if (paymentIntent.status === "succeeded") {
        onSuccess(paymentIntent);
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(err.message || "An error occurred during payment");
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form">
      <div className="mb-4">
        <h6 className="mb-3">Payment Details</h6>
        <div className="border p-3 rounded">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
          />
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <span>Total Amount:</span>
        <span className="fw-bold">${totalAmount.toFixed(2)}</span>
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      <div className="d-flex justify-content-between">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={isProcessing}
        >
          <i className="fa fa-arrow-left me-2"></i> Back to Cart
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!stripe || isProcessing}
        >
          {isProcessing ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Processing...
            </>
          ) : (
            <>
              <i className="fa fa-credit-card me-2"></i> Pay Now
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Payment Modal Component
const PaymentModal = ({ show, totalAmount, onSuccess, onClose }) => {
  if (!show) return null;

  return (
    <div
      className="modal-backdrop"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
      }}
    >
      <div
        className="modal-content bg-white p-4 rounded shadow-lg"
        style={{
          width: "100%",
          maxWidth: "500px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">
            <i className="fa fa-credit-card me-2 text-primary"></i>
            Secure Checkout
          </h3>
          <button
            onClick={onClose}
            className="btn btn-link text-dark p-0"
            style={{ fontSize: "1.5rem" }}
          >
            &times;
          </button>
        </div>

        <Elements stripe={stripePromise}>
          <CheckoutForm
            totalAmount={totalAmount}
            onSuccess={onSuccess}
            onCancel={onClose}
          />
        </Elements>

        {/* <div className="mt-3 text-center">
          <img
            src="https://stripe.com/img/v3/payments/overview/photos/payments-visa.svg"
            alt="Visa"
            className="me-2"
            style={{ height: "26px" }}
          />
          <img
            src="https://stripe.com/img/v3/payments/overview/photos/payments-mastercard.svg"
            alt="Mastercard"
            className="me-2"
            style={{ height: "26px" }}
          />
          <img
            src="https://stripe.com/img/v3/payments/overview/photos/payments-amex.svg"
            alt="American Express"
            className="me-2"
            style={{ height: "26px" }}
          />
          <img
            src="https://stripe.com/img/v3/payments/overview/photos/payments-discover.svg"
            alt="Discover"
            style={{ height: "26px" }}
          />
        </div> */}

        <div className="mt-3 text-center">
          {/* Visa */}
          <svg
            className="me-2"
            width="40"
            height="25"
            viewBox="0 0 40 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M17.3 8.5H14.4L12 16.5H14.9L17.3 8.5Z" fill="#1A1F71" />
            <path
              d="M28.7 8.7C28 8.5 27 8.4 25.8 8.4C23 8.4 21 9.8 21 12C21 13.9 22.7 14.8 24.2 15.3C25.7 15.8 26.2 16.2 26.2 16.8C26.2 17.6 25.2 18.1 23.9 18.1C22.4 18.1 21.5 17.9 20.3 17.5L20 17.4L19.6 19.8C20.5 20.1 21.9 20.4 23.5 20.4C26.6 20.4 28.6 19 28.6 16.5C28.6 14.4 26.8 13.5 25 12.9C23.6 12.4 23.1 12.1 23.1 11.5C23.1 10.9 23.8 10.4 25.1 10.4C26.2 10.4 27.1 10.6 27.8 10.8L28 10.9L28.7 8.7Z"
              fill="#1A1F71"
            />
            <path
              d="M32.9 8.5H30.4C29.6 8.5 29 8.8 28.7 9.6L24.5 16.5H27.6C27.6 16.5 28.1 15.2 28.2 14.9H31.6C31.7 15.3 32.3 16.5 32.3 16.5H35.1L32.9 8.5ZM29 13.6C29.3 12.8 30.3 10.6 30.3 10.6C30.3 10.6 30.3 10.5 30.3 10.6L30.6 13.6H29Z"
              fill="#1A1F71"
            />
            <path
              d="M9.3 8.5L6.4 16.5H9.3L10 14H13.2L13.8 16.5H16.6L13.7 8.5H9.3ZM10.5 12.2L11.5 9.3C11.5 9.3 11.7 8.7 11.9 8.1H12C12.2 8.7 12.6 9.9 12.6 9.9L13.4 12.2H10.5Z"
              fill="#1A1F71"
            />
          </svg>

          {/* Mastercard */}
          <svg
            className="me-2"
            width="40"
            height="25"
            viewBox="0 0 40 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M24.5 19.5C28.6 19.5 32 16.1 32 12C32 7.9 28.6 4.5 24.5 4.5C22.5 4.5 20.7 5.3 19.4 6.6C18.1 7.9 17.3 9.7 17.3 11.7C17.3 13.7 18.1 15.5 19.4 16.8C20.7 18.1 22.5 19 24.5 19V19.5Z"
              fill="#FF5F00"
            />
            <path
              d="M15.5 19.5C19.6 19.5 23 16.1 23 12C23 7.9 19.6 4.5 15.5 4.5C13.5 4.5 11.7 5.3 10.4 6.6C9.1 7.9 8.3 9.7 8.3 11.7C8.3 13.7 9.1 15.5 10.4 16.8C11.7 18.1 13.5 19 15.5 19V19.5Z"
              fill="#EB001B"
            />
            <path
              d="M19.4 16.8C20.7 15.5 21.5 13.7 21.5 11.7C21.5 9.7 20.7 7.9 19.4 6.6C18.1 5.3 16.3 4.5 14.3 4.5H15.5C17.5 4.5 19.3 5.3 20.6 6.6C21.9 7.9 22.7 9.7 22.7 11.7C22.7 13.7 21.9 15.5 20.6 16.8C19.3 18.1 17.5 19 15.5 19H14.3C16.3 19 18.1 18.1 19.4 16.8Z"
              fill="#F79E1B"
            />
          </svg>

          {/* American Express */}
          <svg
            className="me-2"
            width="40"
            height="25"
            viewBox="0 0 40 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 4C0 1.8 1.8 0 4 0H36C38.2 0 40 1.8 40 4V21C40 23.2 38.2 25 36 25H4C1.8 25 0 23.2 0 21V4Z"
              fill="#016FD0"
            />
            <path
              d="M10.6 15.8H8.9L7.9 12.9H7.8L6.8 15.8H5.1L3.3 9.2H5.1L6.1 12.8H6.2L7.3 9.2H8.5L9.6 12.8H9.7L10.7 9.2H12.5L10.6 15.8ZM14.7 15.8V9.2H16.4V15.8H14.7ZM19.6 15.8H17.9V9.2H19.6V15.8ZM25.6 15.8H23.9L22.9 12.9H22.8L21.8 15.8H20.1L18.3 9.2H20.1L21.1 12.8H21.2L22.3 9.2H23.5L24.6 12.8H24.7L25.7 9.2H27.5L25.6 15.8ZM31.7 15.8H29.9V9.2H31.7V15.8ZM36.7 15.8H34.9V9.2H36.7V15.8Z"
              fill="white"
            />
          </svg>

          {/* Discover */}
          <svg
            width="40"
            height="25"
            viewBox="0 0 40 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 4C0 1.8 1.8 0 4 0H36C38.2 0 40 1.8 40 4V21C40 23.2 38.2 25 36 25H4C1.8 25 0 23.2 0 21V4Z"
              fill="#231F20"
            />
            <path
              d="M12.4 9.2H8.6V15.8H12.5C14.4 15.8 15.9 14.3 15.9 12.5C15.9 10.7 14.4 9.2 12.4 9.2ZM12.3 14.3H10.3V10.7H12.3C13.3 10.7 14.1 11.5 14.1 12.5C14.1 13.5 13.3 14.3 12.3 14.3ZM20.2 12.5C20.2 10.7 21.7 9.2 23.6 9.2H27.5V15.8H23.6C21.7 15.8 20.2 14.3 20.2 12.5ZM25.8 14.3V10.7H23.8C22.8 10.7 22 11.5 22 12.5C22 13.5 22.8 14.3 23.8 14.3H25.8ZM31.7 15.8H29.9V9.2H31.7V15.8Z"
              fill="white"
            />
            <path
              d="M17.8 12.5C17.8 13.5 18.6 14.3 19.6 14.3H21.6V10.7H19.6C18.6 10.7 17.8 11.5 17.8 12.5Z"
              fill="#FF6000"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

// Main ShoppingCart Component
const ShoppingCart = () => {
  const {
    cartItems,
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
    clearCart,
  } = useCartContext();
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Helper function to safely convert to number
  const toNumber = (value) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const numStr = value.replace(/[^0-9.]/g, "");
      return parseFloat(numStr) || 0;
    }
    return 0;
  };

  // Safely calculate the total amount
  const totalAmount = cartItems.reduce((total, item) => {
    const price = toNumber(item.price);
    const quantity = item.quantity || 1;
    return total + price * quantity;
  }, 0);

  // Calculate total items
  const totalItems = cartItems.reduce(
    (acc, item) => acc + (item.quantity || 1),
    0
  );

  const handlePaymentSuccess = (paymentIntent) => {
    setShowPaymentModal(false);
    Swal.fire({
      title: "Payment Successful!",
      text: `Your payment of $${(paymentIntent.amount / 100).toFixed(
        2
      )} was completed.`,
      icon: "success",
      confirmButtonText: "Continue Shopping",
      confirmButtonColor: "#0d6efd",
      backdrop: `
        rgba(0,0,123,0.4)
        url("/images/nyan-cat.gif")
        left top
        no-repeat
      `,
    }).then(() => {
      // Clear the cart after successful payment
      clearCart();
    });
  };

  const processPayment = () => {
    if (totalAmount <= 0) {
      Swal.fire({
        title: "Invalid Amount",
        text: "Your cart total must be greater than $0.00",
        icon: "error",
      });
      return;
    }
    setShowPaymentModal(true);
  };

  // Helper function to safely format prices
  const formatPrice = (price) => {
    const num = toNumber(price);
    return isNaN(num) ? "$0.00" : `$${num.toFixed(2)}`;
  };

  return (
    <div className="bg-light min-vh-100 py-5">
      {/* Payment Modal */}
      <PaymentModal
        show={showPaymentModal}
        totalAmount={totalAmount}
        onSuccess={handlePaymentSuccess}
        onClose={() => setShowPaymentModal(false)}
      />

      <div className="container">
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/" className="text-decoration-none">
                    <i className="fa fa-home"></i> Home
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Shopping Cart
                </li>
              </ol>
            </nav>
            <h1 className="display-5 fw-bold">
              <i className="fa fa-shopping-cart text-primary me-2"></i>
              Your Shopping Cart
            </h1>
            <p className="text-muted">
              {cartItems.length === 0
                ? "Your cart is currently empty"
                : `You have ${totalItems} ${
                    totalItems === 1 ? "item" : "items"
                  } in your cart`}
            </p>
          </div>
        </div>

        <div className="row g-4">
          {/* Left Column - Cart Items */}
          <div className="col-lg-8">
            {cartItems.length === 0 ? (
              <div className="card shadow-sm border-0">
                <div className="card-body text-center py-5">
                  <i className="fa fa-shopping-cart fa-4x text-muted mb-4"></i>
                  <h3 className="mb-3">Your cart is empty</h3>
                  <p className="text-muted mb-4">
                    Looks like you haven't added anything to your cart yet
                  </p>
                  <Link
                    to="/api/courses"
                    className="btn btn-primary btn-lg px-4"
                  >
                    <i className="fa fa-book me-2"></i> Browse Courses
                  </Link>
                </div>
              </div>
            ) : (
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0">
                    <i className="fa fa-list-alt me-2 text-primary"></i>
                    Cart Items ({totalItems})
                  </h5>
                </div>
                <div className="card-body p-0">
                  <div className="list-group list-group-flush">
                    {cartItems.map((item) => {
                      const price = toNumber(item.price);
                      const quantity = item.quantity || 1;
                      const itemTotal = price * quantity;

                      return (
                        <div
                          key={item.id}
                          className="list-group-item border-0 border-bottom"
                        >
                          <div className="row align-items-center g-3">
                            <div className="col-md-2 d-none d-md-block">
                              <div className="bg-light rounded p-2 text-center">
                                <i className="fa fa-book fa-3x text-muted"></i>
                              </div>
                            </div>
                            <div className="col-md-6 col-12">
                              <h5 className="mb-1">{item.title}</h5>
                              <p className="text-muted mb-2">
                                {formatPrice(price)}
                              </p>
                              <div className="d-flex align-items-center">
                                <button
                                  className="btn btn-outline-secondary btn-sm me-2"
                                  onClick={() => decreaseQuantity(item)}
                                  disabled={quantity <= 1}
                                >
                                  <i className="fa fa-minus"></i>
                                </button>
                                <span className="fw-bold">{quantity}</span>
                                <button
                                  className="btn btn-outline-secondary btn-sm ms-2"
                                  onClick={() => increaseQuantity(item)}
                                >
                                  <i className="fa fa-plus"></i>
                                </button>
                              </div>
                            </div>
                            <div className="col-md-3 col-6 text-md-center">
                              <h5 className="mb-0 text-success">
                                {formatPrice(itemTotal)}
                              </h5>
                            </div>
                            <div className="col-md-1 col-6 text-end">
                              <button
                                className="btn btn-link text-danger"
                                onClick={() => removeFromCart(item)}
                                title="Remove item"
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Order Summary */}
          {cartItems.length > 0 && (
            <div className="col-lg-4">
              <div
                className="card shadow-sm border-0 sticky-top"
                style={{ top: "20px" }}
              >
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0">
                    <i className="fa fa-receipt me-2 text-primary"></i>
                    Order Summary
                  </h5>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Subtotal:</span>
                    <span>{formatPrice(totalAmount)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Discount:</span>
                    <span className="text-success">$0.00</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Tax:</span>
                    <span>$0.00</span>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between mb-4">
                    <h5 className="mb-0">Total:</h5>
                    <h4 className="text-success">{formatPrice(totalAmount)}</h4>
                  </div>
                  <button
                    className="btn btn-primary btn-lg w-100 mb-3"
                    onClick={processPayment}
                  >
                    <i className="fa fa-credit-card me-2"></i> Proceed to
                    Checkout
                  </button>
                  <Link
                    to="/api/courses"
                    className="btn btn-outline-secondary btn-lg w-100"
                  >
                    <i className="fa fa-arrow-left me-2"></i> Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Empty Cart Recommendation */}
        {cartItems.length === 0 && (
          <div className="row mt-5">
            <div className="col-12">
              <h4 className="mb-4">You might be interested in:</h4>
              <div className="row g-4">
                {[1, 2, 3].map((item) => (
                  <div className="col-md-4" key={item}>
                    <div className="card shadow-sm h-100">
                      <div className="card-body text-center">
                        <div className="bg-light rounded p-3 mb-3">
                          <i className="fa fa-book fa-3x text-primary"></i>
                        </div>
                        <h5>Recommended Course {item}</h5>
                        <p className="text-muted">$99.99</p>
                        <button className="btn btn-outline-primary w-100">
                          <i className="fa fa-plus me-2"></i> Add to Cart
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShoppingCart;
